import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { TextMaskConfig } from 'angular2-text-mask';
import { Observable } from 'rxjs';

import { BaseComponent } from '../../../abstracts/base.abstract';
import { featuresWithPermissions } from '../../../data/permissions';
import { BasicInputTypeEnum } from '../../../enums/basic-input-type.enum';
import { ContactTypeEnum } from '../../../enums/contact-type.enum';
import { EntityTypeEnum } from '../../../enums/entity-type.enum';
import { ISelectChoices } from '../../../interfaces/generic-select-config.interface';
import { IClass } from '../../../interfaces/groups/class.interface';
import { ISchool } from '../../../interfaces/groups/school.interface';
import { IQuestionChoices } from '../../../interfaces/radio-checkbox-config.interface';
import { IUser } from '../../../interfaces/users/user.interface';
import { AuthState } from '../../../store/state/auth.state';
import { ClassesState } from '../../../store/state/classes.state';
import { EnumsState } from '../../../store/state/enums.state';
import { SchoolsState } from '../../../store/state/schools.state';
import { formatPhoneNumber } from '../../../utils/phone-formatter.utils';

@Component({
    selector: 'app-personal-data',
    templateUrl: './personal-data.component.html',
    styleUrls: ['./personal-data.component.scss'],
})
export class PersonalDataComponent extends BaseComponent implements OnInit {
    @Select(AuthState.currentUser) public currentUser$: Observable<IUser>;

    @Select(SchoolsState.getAllSchools) public schools$: Observable<ISchool[]>;
    @Select(ClassesState.getCurrentYearClasses) public classes$: Observable<IClass[]>;

    @Select(EnumsState.adultGenders) public adultGenders$: Observable<IQuestionChoices[]>;
    @Select(EnumsState.studentGenders) public studentGenders$: Observable<IQuestionChoices[]>;

    @Select(EnumsState.relativeTypes) public relativeTypesChoices$: Observable<ISelectChoices[]>;
    @Select(EnumsState.rateTypes) public rateTypesChoices$: Observable<ISelectChoices[]>;

    @Select(EnumsState.contactTypes) public contactTypesChoices$: Observable<ISelectChoices[]>;

    @Input() public formGroup: FormGroup;
    @Input() public isEdit: boolean;
    @Input() public readonly = false;
    @Input() public isMyAccount = false;

    @Output() public elementSizeModification = new EventEmitter<void>();

    public basicInputTypeEnum = BasicInputTypeEnum;
    public entityTypeEnum = EntityTypeEnum;
    public contactTypeEnum = ContactTypeEnum;
    public contactType: ContactTypeEnum;

    public displayFields = true;

    public langsChoices: ISelectChoices[] = [
        { label: 'REPERTOIRE.FORMS.PERSONAL-DATA.LANG.FR', value: 'fr' },
        { label: 'REPERTOIRE.FORMS.PERSONAL-DATA.LANG.NL', value: 'nl' },
    ];

    public schoolsChoices: ISelectChoices[] = [];
    public classesChoices: ISelectChoices[] = [];

    public nationalNumberMaskConfig: TextMaskConfig = {
        mask: [/\d/, /\d/, '.', /\d/, /\d/, '.', /\d/, /\d/, '-', /\d/, /\d/, /\d/, '.', /\d/, /\d/],
        guide: false,
    };

    public permissions = featuresWithPermissions;

    constructor(private store: Store) {
        super();
    }

    public ngOnInit(): void {
        if (!this.lang.value) {
            this.lang.patchValue(this.store.selectSnapshot(AuthState.currentUser)?.lang || '', { emitEvent: false });
        }
        this.uns = this.schools$.subscribe((schools: ISchool[]) => {
            this.schoolsChoices = schools.map((school: ISchool) => ({ value: school.id, label: school.name }));
        });
        this.uns = this.classes$.subscribe((classes: IClass[]) => {
            console.log('classes', classes);
            this.classesChoices = classes.map((classZ: IClass) => ({ value: classZ.id, label: classZ.name }));
        });

        if (this.rateType) {
            this.uns = this.rateType.valueChanges.subscribe((rateTypeId: any) => {
                const discount: number = +rateTypeId === 3 ? 20 : +rateTypeId === 2 ? 40 : 0;
                this.discount.patchValue(discount);
            });
        }

        if (this.contactTypeControl) {
            if (this.contactTypeControl.valid) {
                this.displayFields = true;
                this.setContactType(this.contactTypeControl.value);
            } else {
                this.displayFields = false;
            }
            this.uns = this.contactTypeControl.valueChanges.subscribe((contactTypeId: number) => {
                this.setContactType(contactTypeId);
                this.displayFields = true;
            });
        }
    }

    public formatPhoneNumber(phoneNumber: FormControl): void {
        if (phoneNumber.valid) {
            phoneNumber.patchValue(formatPhoneNumber(phoneNumber.value));
        }
    }

    private setContactType(typeId: number): void {
        if (typeId === 1) {
            this.contactType = this.contactTypeEnum.PERSON;
        }
        if (typeId === 2) {
            this.contactType = this.contactTypeEnum.ORGANIZATION;
        }
    }

    public get userType(): string {
        return this.formGroup.get('type').value;
    }

    public get firstname(): FormControl {
        return this.formGroup.get('firstname') as FormControl;
    }

    public get lastname(): FormControl {
        return this.formGroup.get('lastname') as FormControl;
    }

    public get displayName(): FormControl {
        return this.formGroup.get('displayName') as FormControl;
    }

    public get name(): FormControl {
        return this.formGroup.get('name') as FormControl;
    }

    public get email(): FormControl {
        return this.formGroup.get('email') as FormControl;
    }

    public get homeNumber(): FormControl {
        return this.formGroup.get('homeNumber') as FormControl;
    }

    public get mobileNumber(): FormControl {
        return this.formGroup.get('mobileNumber') as FormControl;
    }

    public get gender(): FormControl {
        return this.formGroup.get('gender') as FormControl;
    }

    public get lang(): FormControl {
        return this.formGroup.get('lang') as FormControl;
    }

    public get nationalNumber(): FormControl {
        return this.formGroup.get('nationalNumber') as FormControl;
    }

    public get bankAccount(): FormControl {
        return this.formGroup.get('bankAccount') as FormControl;
    }

    public get birthdate(): FormControl {
        return this.formGroup.get('birthdate') as FormControl;
    }

    public get school(): FormControl {
        return this.formGroup.get('school') as FormControl;
    }

    public get class(): FormControl {
        return this.formGroup.get('class') as FormControl;
    }

    public get rateType(): FormControl {
        return this.formGroup.get('rateType') as FormControl;
    }

    public get discount(): FormControl {
        return this.formGroup.get('discount') as FormControl;
    }

    public get isActive(): FormControl {
        return this.formGroup.get('isActive') as FormControl;
    }

    public get relationship(): FormControl {
        return this.formGroup.get('relationship') as FormControl;
    }

    public get website(): FormControl {
        return this.formGroup.get('website') as FormControl;
    }

    public get contactTypeControl(): FormControl {
        return this.formGroup.get('contactType') as FormControl;
    }
}
